import { Component, OnInit } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { PortfolioMovementService, EnrollmentWithdrawalInfo, WithdrawEnrollmentRequest, PendingInstallment, PortfolioMovementResult } from '../../../services/portfolio-movement.service';
import { ParticipantService } from '../../../services/participant.service';
import { ToastService } from '../../../services/toast.service';

@Component({
  selector: 'app-withdrawal-wizard',
  templateUrl: './withdrawal-wizard.component.html',
  styleUrls: ['./withdrawal-wizard.component.scss']
})
export class WithdrawalWizardComponent implements OnInit {
  currentStep = 1;
  totalSteps = 4; // 🆕 Ahora tiene 4 pasos (incluyendo resumen final)
  
  // Datos del wizard
  participants: any[] = [];
  selectedParticipantId: number | null = null;
  enrollments: EnrollmentWithdrawalInfo[] = [];
  selectedEnrollment: EnrollmentWithdrawalInfo | null = null;
  selectedInstallments: number[] = [];
  
  // Forms
  searchForm: FormGroup;
  confirmForm: FormGroup;
  
  // UI states
  isLoading = false;
  isSearching = false;
  isSubmitting = false;
  searchPerformed = false;
  
  // 🆕 Resultado de la operación (para paso final)
  operationResult: PortfolioMovementResult | null = null;
  operationSuccess = false;
  operationError = '';
  
  // Si viene desde el detalle de inscripción
  preselectedEnrollmentId: number | null = null;

  constructor(
    private fb: FormBuilder,
    private portfolioService: PortfolioMovementService,
    private participantService: ParticipantService,
    private toastService: ToastService,
    private router: Router,
    private route: ActivatedRoute
  ) {
    this.searchForm = this.fb.group({
      searchTerm: ['', Validators.required]
    });
    
    this.confirmForm = this.fb.group({
      reason: ['', [Validators.required, Validators.minLength(10)]],
      notes: ['']
    });
  }

  ngOnInit(): void {
    // Verificar si viene con un enrollmentId preseleccionado
    this.route.queryParams.subscribe(params => {
      if (params['enrollmentId']) {
        this.preselectedEnrollmentId = +params['enrollmentId'];
        this.loadEnrollmentById(this.preselectedEnrollmentId);
      }
    });
  }

  // Helper para extraer datos de estructura paginada o array directo
  private extractData(response: any): any[] {
    if (Array.isArray(response)) return response;
    if (response?.data) {
      return Array.isArray(response.data) ? response.data : (response.data?.data || []);
    }
    return [];
  }

  // Paso 1: Buscar participante
  searchParticipant(): void {
    if (this.searchForm.invalid) return;
    
    this.isSearching = true;
    this.searchPerformed = true;
    const term = this.searchForm.get('searchTerm')?.value;
    
    this.participantService.getAllFiltered(term).subscribe({
      next: (response) => {
        this.participants = this.extractData(response);
        this.isSearching = false;
      },
      error: (err) => {
        this.toastService.showToast('error', 'Error al buscar participantes');
        this.isSearching = false;
      }
    });
  }

  selectParticipant(participant: any): void {
    this.selectedParticipantId = participant.id;
    this.loadActiveEnrollments(participant.id);
  }

  loadActiveEnrollments(participantId: number): void {
    this.isLoading = true;
    this.portfolioService.getActiveEnrollmentsByParticipant(participantId).subscribe({
      next: (response) => {
        this.enrollments = this.extractData(response);
        this.isLoading = false;
        if (this.enrollments.length === 0) {
          this.toastService.showToast('warning', 'Este participante no tiene inscripciones activas');
        }
      },
      error: (err) => {
        this.toastService.showToast('error', 'Error al cargar inscripciones');
        this.isLoading = false;
      }
    });
  }

  loadEnrollmentById(enrollmentId: number): void {
    this.isLoading = true;
    this.portfolioService.getEnrollmentWithdrawalInfo(enrollmentId).subscribe({
      next: (response) => {
        const enrollment = response.data || response;
        if (enrollment && enrollment.canBeWithdrawn) {
          this.selectedEnrollment = enrollment;
          this.currentStep = 2;
        } else {
          this.toastService.showToast('error', 'Esta inscripción no puede ser retirada');
          this.router.navigate(['/enrollments']);
        }
        this.isLoading = false;
      },
      error: (err) => {
        this.toastService.showToast('error', 'Error al cargar inscripción');
        this.isLoading = false;
      }
    });
  }

  selectEnrollment(enrollment: EnrollmentWithdrawalInfo): void {
    if (!enrollment.canBeWithdrawn) {
      this.toastService.showToast('warning', 'Esta inscripción no tiene cuotas pendientes para castigar');
      return;
    }
    this.selectedEnrollment = enrollment;
    this.selectedInstallments = enrollment.pendingPaymentPlans
      .filter(pp => !pp.isPunished)
      .map(pp => pp.paymentPlanId);
    this.currentStep = 2;
  }

  // Paso 2: Seleccionar cuotas a castigar
  toggleInstallment(paymentPlanId: number): void {
    const index = this.selectedInstallments.indexOf(paymentPlanId);
    if (index > -1) {
      this.selectedInstallments.splice(index, 1);
    } else {
      this.selectedInstallments.push(paymentPlanId);
    }
  }

  selectAllInstallments(): void {
    if (!this.selectedEnrollment) return;
    this.selectedInstallments = this.selectedEnrollment.pendingPaymentPlans
      .filter(pp => !pp.isPunished)
      .map(pp => pp.paymentPlanId);
  }

  deselectAllInstallments(): void {
    this.selectedInstallments = [];
  }

  isInstallmentSelected(paymentPlanId: number): boolean {
    return this.selectedInstallments.includes(paymentPlanId);
  }

  get selectedTotalAmount(): number {
    if (!this.selectedEnrollment) return 0;
    return this.selectedEnrollment.pendingPaymentPlans
      .filter(pp => this.selectedInstallments.includes(pp.paymentPlanId))
      .reduce((sum, pp) => sum + pp.pendingAmount, 0);
  }

  get allInstallmentsSelected(): boolean {
    if (!this.selectedEnrollment) return false;
    const availableInstallments = this.selectedEnrollment.pendingPaymentPlans.filter(pp => !pp.isPunished);
    return this.selectedInstallments.length === availableInstallments.length && availableInstallments.length > 0;
  }

  toggleSelectAll(): void {
    if (this.allInstallmentsSelected) {
      this.deselectAllInstallments();
    } else {
      this.selectAllInstallments();
    }
  }

  // Paso 3: Confirmar
  goToStep(step: number): void {
    if (step < this.currentStep) {
      this.currentStep = step;
    } else if (step === 2 && this.selectedEnrollment) {
      this.currentStep = 2;
    } else if (step === 3 && this.selectedInstallments.length > 0) {
      this.currentStep = 3;
    }
  }

  nextStep(): void {
    if (this.currentStep === 1 && this.selectedEnrollment) {
      this.currentStep = 2;
    } else if (this.currentStep === 2 && this.selectedInstallments.length > 0) {
      this.currentStep = 3;
    }
  }

  prevStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
    }
  }

  submitWithdrawal(): void {
    if (this.confirmForm.invalid || !this.selectedEnrollment || this.isSubmitting) return;
    
    this.isSubmitting = true;
    const request: WithdrawEnrollmentRequest = {
      enrollmentId: this.selectedEnrollment.enrollmentId,
      reason: this.confirmForm.get('reason')?.value,
      paymentPlanIds: this.selectedInstallments,
      notes: this.confirmForm.get('notes')?.value
    };

    this.portfolioService.withdrawEnrollment(request).subscribe({
      next: (response) => {
        const result = response.data || response;
        this.operationResult = result;
        this.isSubmitting = false;
        
        if (result.success) {
          this.operationSuccess = true;
          this.currentStep = 4; // 🆕 Ir al paso de resumen final
        } else {
          this.operationSuccess = false;
          this.operationError = result.message || 'Error al procesar retiro';
          this.currentStep = 4;
        }
      },
      error: (err) => {
        this.operationSuccess = false;
        this.operationError = 'Error al procesar retiro. Por favor, intente nuevamente.';
        this.currentStep = 4;
        this.isSubmitting = false;
      }
    });
  }

  cancel(): void {
    this.router.navigate(['/enrollments']);
  }

  // 🆕 Métodos para el paso final
  viewEnrollment(): void {
    if (this.selectedEnrollment) {
      this.router.navigate(['/enrollments/view', this.selectedEnrollment.enrollmentId]);
    }
  }

  goToEnrollmentsList(): void {
    this.router.navigate(['/enrollments']);
  }

  startNewWithdrawal(): void {
    // Reiniciar el wizard
    this.currentStep = 1;
    this.selectedParticipantId = null;
    this.selectedEnrollment = null;
    this.selectedInstallments = [];
    this.enrollments = [];
    this.participants = [];
    this.searchPerformed = false;
    this.operationResult = null;
    this.operationSuccess = false;
    this.operationError = '';
    this.searchForm.reset();
    this.confirmForm.reset();
  }
}
